import { ComponentPropsWithoutRef } from 'react';
import Markdown from 'shared/components/Markdown';
import { FONT_SIZES, HEIGHTS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { materialFieldSSTStyles, materialLabelStyles } from 'inquiry/mixins/form';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputTextAreaComponent } from 'lib/interfaces/ui-components';

export type TextAreaProps = ComponentPropsWithoutRef<'textarea'> &
  FormControlProps & {
    styles: InputTextAreaComponent['styles'] | undefined;
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  };

const TextArea = styled('textarea')<
  TextAreaProps & {
    focused: boolean | undefined;
    hasError: boolean | undefined;
    hasWarning: boolean | undefined;
  }
>`
  ${materialFieldSSTStyles}
  height: ${(props) => (props.rows ? 'unset' : HEIGHTS.textArea)};
  resize: ${(props) => props.resize || 'none'};
`;

const StyledMarkdown = styled(Markdown)<
  TextAreaProps & {
    focused: boolean | undefined;
    hasValue: boolean | undefined;
    hasError: boolean | undefined;
    hasWarning: boolean | undefined;
  }
>`
  ${materialLabelStyles}

  ${({ hasError, hasValue }) =>
    (hasError || hasValue) &&
    css`
      top: -14px;
      font-size: ${FONT_SIZES.small};
    `};
`;

const Container = styled('div')`
  position: relative;
  background-color: ${({ theme }) => theme.colorModalBackground};
`;

interface MaterialTextAreaProps {
  labelText: string;
  value?: string;
  focused?: boolean;
}

export default function MaterialTextArea({
  className,
  focused,
  hasError,
  hasWarning,
  labelText,
  styles,
  ...passthroughProps
}: MaterialTextAreaProps & TextAreaProps) {
  return (
    <Container className={className}>
      <TextArea
        focused={focused}
        hasError={hasError}
        hasWarning={hasWarning}
        // Avoid spell-jacking
        spellCheck={false}
        styles={styles}
        {...passthroughProps}
      />
      <StyledMarkdown
        wrapper='label'
        wrapperProps={{ htmlFor: passthroughProps.id }}
        focused={focused}
        hasError={hasError}
        hasValue={Boolean(passthroughProps.value)}
        hasWarning={hasWarning}
        styles={styles}
      >
        {labelText}
      </StyledMarkdown>
    </Container>
  );
}
